import { useQueryClient } from '@tanstack/react-query';
import { COLLECTION_METHODS, customerPaymentSchema, PAYMENT_METHOD_LABELS, type CustomerDTO, type PaymentMethod } from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input } from '@bifeai/ui';
import { HandCoins } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { usePosSettings } from '../../lib/customers';
import { formatMoney } from '../../lib/format';
import { newClientId } from '../../lib/pos';
import { useSession } from '../../lib/session';
import { printPaymentReceipt } from '../../lib/ticket-print';
import { centsToPesosInput, pesosToCents } from '../../lib/units';

type Method = Exclude<PaymentMethod, 'ACCOUNT'>;

/** Cobrar una deuda, total o una parte. En efectivo entra a la caja abierta y suma al arqueo. */
export function CollectDialog({ customer, onClose, onDone }: { customer: CustomerDTO; onClose: () => void; onDone: (c: CustomerDTO) => void }) {
  const qc = useQueryClient();
  const { me } = useSession();
  const settings = usePosSettings();
  const enabled = COLLECTION_METHODS.filter((m) => settings.data?.sales.enabledPayments.includes(m) ?? true);
  const [method, setMethod] = useState<Method>('CASH');
  const [amount, setAmount] = useState(centsToPesosInput(customer.balanceCents));
  const [reference, setReference] = useState('');
  const [print, setPrint] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const clientId = useRef(newClientId());
  const cents = pesosToCents(amount) ?? 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = customerPaymentSchema.safeParse({
      clientId: clientId.current,
      method,
      amountCents: cents,
      reference,
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá el importe.');
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ customer: CustomerDTO; movementId: string }>(`/customers/${customer.id}/payments`, parsed.data);
      await Promise.all(['customers', 'cash', 'dashboard', 'notifications'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      const mv = r.customer.movements.find((m) => m.id === r.movementId);
      if (print && mv && settings.data)
        printPaymentReceipt(r.customer, mv, settings.data.ticket.businessName || me.tenant.name, settings.data.ticket);
      onDone(r.customer);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Cobrar a ${customer.name}`}
      description={`Debe ${formatMoney(customer.balanceCents)}.`}
      wide
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-pay-methods" role="radiogroup" aria-label="Medio de pago">
          {enabled.map((m) => (
            <button key={m} type="button" role="radio" aria-checked={method === m} className="bf-pay-method" onClick={() => setMethod(m)}>
              <span>{PAYMENT_METHOD_LABELS[m]}</span>
            </button>
          ))}
        </div>
        <Field label="Importe" hint="Puede pagar todo o una parte.">
          <Input className="bf-input--lg" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </Field>
        <div className="bf-chips">
          {[1, 0.5].map((f) => {
            const v = Math.round((customer.balanceCents * f) / 100) * 100;
            return (
              <button key={f} type="button" className="bf-chip" aria-pressed={cents === v} onClick={() => setAmount(centsToPesosInput(v))}>
                {f === 1 ? 'Todo' : 'La mitad'}: {formatMoney(v)}
              </button>
            );
          })}
        </div>
        {method !== 'CASH' && (
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Referencia (opcional): n.º de operación, últimos 4…"
            aria-label="Referencia"
          />
        )}
        {method === 'CASH' && <Callout>El efectivo entra a tu caja abierta y se suma al arqueo del turno.</Callout>}
        <div className="bf-pos-preview">
          <span>Queda debiendo</span>
          <strong>{formatMoney(Math.max(0, customer.balanceCents - cents))}</strong>
        </div>
        <label className="bf-check">
          <input type="checkbox" checked={print} onChange={(e) => setPrint(e.target.checked)} /> Imprimir recibo
        </label>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" icon={<HandCoins />} loading={busy} disabled={cents <= 0}>
            Cobrar {formatMoney(cents)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
